import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { itemAPI, paymentAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import InfoTooltip from '../components/common/InfoTooltip';
import { Package, Truck, Users, CreditCard, MapPin, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Only initialize Stripe if a real key is configured
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey && !stripeKey.includes('placeholder')
  ? loadStripe(stripeKey)
  : Promise.resolve(null);

const CheckoutForm = ({ item, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'escrow'
  const [purchaseType, setPurchaseType] = useState('shipping');
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fees, setFees] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({ stripeEnabled: false, escrowEnabled: false });

  // Shipping state
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [shippingRateError, setShippingRateError] = useState('');

  // Confirmation
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    fetchFees();
    fetchPaymentConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const response = await paymentAPI.getConfig();
      const cfg = response.data.data;
      setPaymentConfig(cfg);
      // Prefer the in-house (Stripe) escrow flow. Only fall back to Escrow.com
      // if Stripe isn't available but Escrow.com is configured.
      if (cfg.stripeEnabled) {
        setPaymentMethod('stripe');
      } else if (cfg.escrowEnabled) {
        setPaymentMethod('escrow');
      }
    } catch (err) {
      console.error('Error fetching payment config:', err);
    }
  };

  const fetchFees = async () => {
    try {
      const response = await paymentAPI.calculateFees(item._id);
      setFees(response.data.data);
    } catch (err) {
      console.error('Error fetching fees:', err);
    }
  };

  const handleUseSavedAddress = (checked) => {
    setUseSavedAddress(checked);
    if (checked && user?.location) {
      // Auto-fill with user's saved address
      setShippingAddress({
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username,
        street: user.location.street || '',
        city: user.location.city || '',
        state: user.location.state || '',
        zipCode: user.location.zipCode || '',
        phone: user.phone || '',
      });
    } else if (!checked) {
      // Clear the form
      setShippingAddress({
        fullName: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
      });
    }
  };

  const fetchShippingRates = async () => {
    // Only fetch if we have complete address and shipping type is selected
    if (purchaseType !== 'shipping' || !shippingAddress.fullName ||
        !shippingAddress.street || !shippingAddress.city ||
        !shippingAddress.state || !shippingAddress.zipCode) {
      return;
    }

    setLoadingRates(true);
    setShippingRateError('');

    console.log('🚚 Fetching shipping rates...');
    console.log('📦 Item ID:', item._id);
    console.log('📍 To Address:', {
      name: shippingAddress.fullName,
      street1: shippingAddress.street,
      city: shippingAddress.city,
      state: shippingAddress.state,
      zip: shippingAddress.zipCode,
    });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/shipping/calculate-rates`,
        {
          itemId: item._id,
          toAddress: {
            name: shippingAddress.fullName,
            street1: shippingAddress.street,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip: shippingAddress.zipCode,
            country: 'US',
            phone: shippingAddress.phone || '',
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('✅ Shipping rates response:', response.data);

      if (response.data.success) {
        setShippingRates(response.data.data);
        // Auto-select cheapest option
        if (response.data.data.length > 0) {
          setSelectedShippingRate(response.data.data[0]);
          console.log('💰 Selected cheapest rate:', response.data.data[0]);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Unable to calculate shipping rates';
      setShippingRateError(errorMsg);
      console.error('❌ Shipping rates error:', err);
      console.error('❌ Error response:', err.response?.data);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      // Validate confirmation
      if (!confirmed) {
        throw new Error('Please confirm your purchase details');
      }

      // Validate shipping address if shipping purchase
      if (purchaseType === 'shipping') {
        if (!shippingAddress.fullName || !shippingAddress.street ||
            !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode) {
          throw new Error('Please fill in all shipping address fields');
        }

        // Validate shipping rate selection for items with package dimensions
        if (item.packageDimensions?.weight && !selectedShippingRate) {
          throw new Error('Please select a shipping method');
        }
      }

      if (paymentMethod === 'escrow') {
        // Escrow.com payment flow
        const response = await paymentAPI.createEscrowTransaction({
          itemId: item._id,
          purchaseType,
          shippingAddress: purchaseType === 'shipping' ? shippingAddress : undefined,
          // Send the RAW carrier rate; the backend applies the shipping buffer.
          shippingCost: rawShippingCents,
          shippingRateId: selectedShippingRate?.objectId,
        });

        const { transaction, paymentUrl } = response.data.data;

        console.log('✅ Escrow transaction created:', transaction);

        // Redirect to Escrow.com payment page
        if (paymentUrl) {
          window.location.href = paymentUrl;
        } else {
          // If no payment URL, navigate to transaction detail page
          navigate(`/transactions/${transaction._id}`);
        }
      } else {
        // Stripe payment flow
        if (!stripe || !elements) {
          throw new Error('Payment system not loaded. Please refresh the page.');
        }

        // Create payment intent (send RAW rate; backend adds buffer + fee)
        const intentResponse = await paymentAPI.createPaymentIntent({
          itemId: item._id,
          shippingCost: rawShippingCents,
        });

        const { clientSecret } = intentResponse.data.data;

        // Confirm payment
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message);
        }

        // Create transaction
        const transactionResponse = await paymentAPI.createTransaction({
          itemId: item._id,
          stripePaymentId: paymentIntent.id,
          purchaseType,
          shippingAddress: purchaseType === 'shipping' ? shippingAddress : undefined,
          // Send the RAW carrier rate; the backend applies the shipping buffer.
          shippingCost: rawShippingCents,
          shippingRateId: selectedShippingRate?.objectId,
          shippingCarrier: selectedShippingRate?.provider,
          shippingServiceLevel: selectedShippingRate?.servicelevel,
        });

        console.log('✅ Purchase complete:', transactionResponse.data.data);

        // Navigate to transaction detail page
        navigate(`/transactions/${transactionResponse.data.data._id}`);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.response?.data?.error || err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  // Fee config from the server (falls back to defaults). Keeps the displayed
  // breakdown identical to what the backend actually charges.
  const serviceFeePercent = paymentConfig.serviceFeePercent ?? 3;
  const serviceFeeFixedCents = paymentConfig.serviceFeeFixedCents ?? 50;
  const shippingBufferPercent = paymentConfig.shippingBufferPercent ?? 5;

  // Calculate totals including the shipping buffer and buyer service fee.
  const itemPriceCents = fees ? fees.itemPrice : Math.round(item.price * 100);
  // Raw carrier rate (what we send to the backend; backend applies the buffer).
  const rawShippingCents = (purchaseType === 'shipping' && selectedShippingRate)
    ? Math.round(selectedShippingRate.amount * 100)
    : 0;
  // Shipping actually charged to the buyer (raw + buffer).
  const shippingCostCents = rawShippingCents > 0
    ? Math.round(rawShippingCents * (1 + shippingBufferPercent / 100))
    : 0;
  const subtotalCents = itemPriceCents + shippingCostCents;
  const serviceFeeCents = subtotalCents > 0
    ? Math.round(subtotalCents * (serviceFeePercent / 100)) + serviceFeeFixedCents
    : 0;
  const totalCents = subtotalCents + serviceFeeCents;
  const total = totalCents / 100;

  return (
    <form onSubmit={handleSubmit}>
      {/* Payment Method Selection */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Payment Method</h3>

        <div style={{ display: 'grid', gap: '16px' }}>
          {/* Escrow.com Option - only shown when Escrow.com is configured */}
          {paymentConfig.escrowEnabled && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              border: `2px solid ${paymentMethod === 'escrow' ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: paymentMethod === 'escrow' ? 'var(--secondary)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="paymentMethod"
              value="escrow"
              checked={paymentMethod === 'escrow'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={{ cursor: 'pointer' }}
            />
            <Package size={24} style={{ color: 'var(--primary)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Escrow.com (for high-value items)</div>
              <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                Third-party escrow with a 1-day inspection period. Best for expensive items.
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--success)', fontWeight: '500' }}>
                ✓ Licensed third-party escrow service
              </div>
            </div>
          </label>
          )}

          {/* Stripe Option (in-house escrow) - shown when Stripe is configured */}
          {paymentConfig.stripeEnabled && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                border: `2px solid ${paymentMethod === 'stripe' ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: paymentMethod === 'stripe' ? 'var(--secondary)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="paymentMethod"
                value="stripe"
                checked={paymentMethod === 'stripe'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ cursor: 'pointer' }}
              />
              <CreditCard size={24} style={{ color: 'var(--primary)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Credit Card (Recommended)</div>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Pay directly with your card. Funds are held in escrow with 7-day buyer protection.
                </div>
                <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--success)', fontWeight: '500' }}>
                  ✓ Secure checkout ✓ 7-day inspection guarantee
                </div>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Purchase Type Selection */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Purchase Method</h3>

        <div style={{ display: 'grid', gap: '16px' }}>
          {/* Shipping Option */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              border: `2px solid ${purchaseType === 'shipping' ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: purchaseType === 'shipping' ? 'var(--secondary)' : 'transparent',
            }}
          >
            <input
              type="radio"
              name="purchaseType"
              value="shipping"
              checked={purchaseType === 'shipping'}
              onChange={(e) => setPurchaseType(e.target.value)}
              style={{ cursor: 'pointer' }}
            />
            <Truck size={24} style={{ color: 'var(--primary)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Ship to Me</div>
              <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                Seller ships item with tracking. {paymentMethod === 'escrow' ? '1-day' : '7-day'} inspection period.
              </div>
              {item.shippingOptions?.shipping && (
                <div style={{ fontSize: '14px', marginTop: '4px', fontWeight: '500' }}>
                  + ${item.shippingOptions.shippingPrice} shipping
                </div>
              )}
            </div>
          </label>

          {/* In-Person Option */}
          {item.shippingOptions?.localPickup && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                border: `2px solid ${purchaseType === 'in-person' ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: purchaseType === 'in-person' ? 'var(--secondary)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="purchaseType"
                value="in-person"
                checked={purchaseType === 'in-person'}
                onChange={(e) => setPurchaseType(e.target.value)}
                style={{ cursor: 'pointer' }}
              />
              <Users size={24} style={{ color: 'var(--primary)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Meet in Person</div>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Arrange to meet the seller. Confirm receipt immediately.
                </div>
                <div style={{ fontSize: '14px', marginTop: '4px', color: 'var(--success)', fontWeight: '500' }}>
                  No shipping cost
                </div>
              </div>
            </label>
          )}

          {/* Store Pickup Option - Business accounts only */}
          {item.user?.accountType === 'business' && item.user?.allowStorePickup && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                border: `2px solid ${purchaseType === 'store-pickup' ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: purchaseType === 'store-pickup' ? 'var(--secondary)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="purchaseType"
                value="store-pickup"
                checked={purchaseType === 'store-pickup'}
                onChange={(e) => setPurchaseType(e.target.value)}
                style={{ cursor: 'pointer' }}
              />
              <Package size={24} style={{ color: 'var(--primary)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Pick Up from Store</div>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Pick up from {item.user?.storeLocation?.storeName || item.user?.businessInfo?.businessName}
                </div>
                {item.user?.storeLocation?.city && (
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    📍 {item.user.storeLocation.city}, {item.user.storeLocation.state}
                  </div>
                )}
                <div style={{ fontSize: '14px', marginTop: '4px', color: 'var(--success)', fontWeight: '500' }}>
                  No shipping cost
                </div>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Shipping Address Form */}
      {purchaseType === 'shipping' && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={20} />
            Shipping Address
          </h3>

          {/* Use Saved Address Checkbox */}
          {user?.location?.street && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '12px',
                background: 'var(--secondary)',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={useSavedAddress}
                onChange={(e) => handleUseSavedAddress(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500' }}>
                Use my saved address ({user.location.city}, {user.location.state})
              </span>
            </label>
          )}

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Full Name *
              </label>
              <input
                type="text"
                value={shippingAddress.fullName}
                onChange={(e) => setShippingAddress({ ...shippingAddress, fullName: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Street Address *
              </label>
              <input
                type="text"
                value={shippingAddress.street}
                onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                className="input"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  City *
                </label>
                <input
                  type="text"
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  State *
                </label>
                <input
                  type="text"
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                  className="input"
                  maxLength="2"
                  placeholder="CA"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  ZIP Code *
                </label>
                <input
                  type="text"
                  value={shippingAddress.zipCode}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, zipCode: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Phone *
                </label>
                <input
                  type="tel"
                  value={shippingAddress.phone}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                  className="input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Package dimensions check */}
          {item.packageDimensions?.weight ? (
            // Button to calculate shipping rates
            shippingRates.length === 0 ? (
              <button
                type="button"
                onClick={fetchShippingRates}
                disabled={loadingRates || !shippingAddress.fullName || !shippingAddress.street ||
                         !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode}
                className="btn btn-primary"
                style={{ marginTop: '16px', width: '100%' }}
              >
                {loadingRates ? 'Calculating Shipping...' : 'Calculate Shipping Rates'}
              </button>
            ) : null
          ) : (
            // Show message when package dimensions are missing
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '6px',
              fontSize: '14px',
            }}>
              <strong>Note:</strong> The seller hasn't set package dimensions for this item.
              Shipping costs will be determined after purchase. You can contact the seller for an estimate.
            </div>
          )}
        </div>
      )}

      {/* Shipping Rate Selection */}
      {purchaseType === 'shipping' && item.packageDimensions?.weight && shippingRates.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Truck size={20} />
            Select Shipping Method
          </h3>

          {shippingRateError && (
            <div style={{ padding: '12px', background: 'var(--error)', color: 'white', borderRadius: '6px', marginBottom: '16px' }}>
              {shippingRateError}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {shippingRates.map((rate) => (
              <label
                key={rate.objectId}
                style={{
                  padding: '16px',
                  border: `2px solid ${selectedShippingRate?.objectId === rate.objectId ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: selectedShippingRate?.objectId === rate.objectId ? 'var(--primaryLight)' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <input
                  type="radio"
                  name="shippingRate"
                  checked={selectedShippingRate?.objectId === rate.objectId}
                  onChange={() => setSelectedShippingRate(rate)}
                  style={{ cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {rate.provider} - {rate.servicelevel}
                  </div>
                  {rate.estimatedDays && (
                    <div style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
                      Estimated delivery: {rate.estimatedDays} {rate.estimatedDays === 1 ? 'day' : 'days'}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)' }}>
                  ${(Math.round(rate.amount * 100 * (1 + shippingBufferPercent / 100)) / 100).toFixed(2)}
                </div>
              </label>
            ))}
          </div>

          <button
            type="button"
            onClick={fetchShippingRates}
            disabled={loadingRates}
            className="btn"
            style={{ marginTop: '16px', width: '100%' }}
          >
            Recalculate Shipping Rates
          </button>
        </div>
      )}

      {/* Escrow Notice */}
      <div className="card" style={{ marginBottom: '24px', backgroundColor: 'var(--secondary)' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Clock size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>Buyer Protection</div>
            <div style={{ fontSize: '14px', color: 'var(--textSecondary)', lineHeight: '1.5' }}>
              {paymentMethod === 'escrow' ? (
                purchaseType === 'shipping' ? (
                  <>
                    Your payment is held securely by Escrow.com, a licensed third-party escrow service.
                    The seller receives payment only after you accept the item or after a 1-day inspection period.
                    You can reject the item if there's an issue.
                  </>
                ) : (
                  <>
                    Your payment is held securely by Escrow.com until you meet in person and confirm receipt.
                    You'll inspect the item and release funds immediately if satisfied.
                  </>
                )
              ) : (
                purchaseType === 'shipping' ? (
                  <>
                    Your payment is held securely in our internal escrow. The seller receives payment only after you confirm receipt
                    or after a 7-day inspection period. You can open a dispute if there's an issue.
                  </>
                ) : (
                  <>
                    Your payment is held securely until you meet in person and confirm receipt.
                    You'll release funds immediately after inspecting the item.
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      {paymentMethod === 'stripe' ? (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CreditCard size={20} />
            Payment Information
          </h3>

          <div style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            <CardElement options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }} />
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: '24px', backgroundColor: '#f0f9ff', border: '2px solid var(--primary)' }}>
          <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={20} style={{ color: 'var(--primary)' }} />
            Escrow.com Payment
          </h3>
          <div style={{ fontSize: '14px', color: 'var(--textSecondary)', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '12px' }}>
              After clicking "Continue to Escrow.com", you'll be redirected to Escrow.com's secure payment page where you can:
            </p>
            <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
              <li>Pay via credit card, wire transfer, or other payment methods</li>
              <li>View complete transaction details and timeline</li>
              <li>Track the status of your escrow transaction</li>
            </ul>
            <p style={{ fontSize: '13px', fontWeight: '500', color: 'var(--primary)' }}>
              ✓ Licensed and regulated escrow service<br/>
              ✓ Used by millions of buyers and sellers worldwide
            </p>
          </div>
        </div>
      )}

      {/* Order Summary */}
      {fees && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Order Summary</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Item Price</span>
              <span>${(itemPriceCents / 100).toFixed(2)}</span>
            </div>

            {purchaseType === 'shipping' && selectedShippingRate && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Shipping ({selectedShippingRate.provider} {selectedShippingRate.servicelevel})
                  <InfoTooltip text="Calculated live from the carrier based on the seller's location and the package size/weight, plus a small handling buffer. A prepaid label is generated automatically for the seller." />
                </span>
                <span>${(shippingCostCents / 100).toFixed(2)}</span>
              </div>
            )}

            {serviceFeeCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Service Fee
                  <InfoTooltip text="This fee covers secure payment processing (card fees) and buyer protection — your money is held safely until you confirm the item arrived as described. It helps keep BabyResell running." />
                </span>
                <span>${(serviceFeeCents / 100).toFixed(2)}</span>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '700' }}>
                <span>Total</span>
                <span style={{ color: 'var(--primary)' }}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Purchase Confirmation */}
      <div className="card" style={{ marginBottom: '24px', background: 'var(--backgroundSecondary)' }}>
        <label style={{ display: 'flex', alignItems: 'start', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ marginTop: '4px', cursor: 'pointer' }}
            required
          />
          <div style={{ flex: 1, fontSize: '14px' }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              I confirm the purchase details are correct
            </div>
            <div style={{ color: 'var(--textSecondary)' }}>
              {purchaseType === 'shipping' ? (
                <>
                  Item will be shipped to the address above
                  {selectedShippingRate && ` via ${selectedShippingRate.provider} ${selectedShippingRate.servicelevel}`}.
                  Total amount to be charged: ${total.toFixed(2)}
                </>
              ) : (
                <>
                  I will arrange to meet the seller in person. Total amount: ${total.toFixed(2)}
                </>
              )}
            </div>
          </div>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="btn btn-primary"
        disabled={
          loading ||
          !confirmed ||
          (paymentMethod === 'stripe' && !stripe) ||
          (purchaseType === 'shipping' && item.packageDimensions?.weight && !selectedShippingRate)
        }
        style={{ width: '100%', fontSize: '16px', padding: '16px' }}
      >
        {loading ? 'Processing...' : (
          paymentMethod === 'escrow'
            ? `Continue to Escrow.com - $${total.toFixed(2)}`
            : `Complete Purchase - $${total.toFixed(2)}`
        )}
      </button>

      <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--textSecondary)', textAlign: 'center' }}>
        {paymentMethod === 'escrow' ? (
          <>Secure payment powered by Escrow.com. You'll complete payment on their secure platform.</>
        ) : (
          <>Secure payment powered by Stripe. Your card information is encrypted.</>
        )}
      </div>
    </form>
  );
};

const Checkout = () => {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      const response = await itemAPI.getItem(itemId);
      setItem(response.data.data);
    } catch (err) {
      setError('Failed to load item');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !item) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="card">
          <h2>Error</h2>
          <p className="text-muted">{error || 'Item not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px', maxWidth: '800px' }}>
      <h1 style={{ marginBottom: '24px' }}>Checkout</h1>

      {/* Item Summary */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <img
            src={item.images?.[0]?.thumbnail || item.images?.[0]?.fullSize}
            alt={item.title}
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
          <div style={{ flex: 1 }}>
            <h3 style={{ marginBottom: '8px' }}>{item.title}</h3>
            <p style={{ color: 'var(--textSecondary)', marginBottom: '8px', fontSize: '14px' }}>
              Sold by {item.user?.username}
            </p>
            <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>
              ${item.price}
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Form */}
      <Elements stripe={stripePromise}>
        <CheckoutForm item={item} />
      </Elements>
    </div>
  );
};

export default Checkout;
